import {useEffect, useMemo} from 'react';
import useInteractiveWidgetSubmit from '../../hooks/useInteractiveWidgetSubmit';
import {ScheduleSelector} from '../../../../shared/campaign/ScheduleSelector';
import {useCampaignRuntimeContext} from '../../context/CampaignRuntimeContext';
import {useAgentChatContext} from '../../context/AgentChatContext';
import {
  buildScheduleConfigCompletionMessage,
  CAMPAIGN_WIDGET_TOOL_NAMES,
} from '../../utils/campaignCompletionMessages';
import {mergeScheduleArtsWithSession} from '../../../../shared/campaign/utils/scheduleDateTime';

function normalizeScheduleArt(art, index = 0) {
  const pieceNumber = art.piece_number ?? art.pieceNumber ?? index + 1;
  return {
    ...art,
    piece_number: pieceNumber,
    pieceNumber,
    imageUrl: art.imageUrl || art.url,
    url: art.url || art.imageUrl,
    legend: art.legend || art.legenda || '',
    publicationDateTime: art.publicationDateTime,
    socialMedias: art.socialMedias,
  };
}

export default function ScheduleIntegration({
  arts = [],
  isReadOnly = false,
  toolCallId = null,
  onPreviewArt = null,
}) {
  const {sessionDetail} = useAgentChatContext();
  const {
    artScheduleData,
    ensureArtScheduleEntry,
    setSchedulePreviewArt,
    socialScheduleContext,
    scheduleUx,
  } = useCampaignRuntimeContext();

  const {handleSubmit: handleComplete, isPending} = useInteractiveWidgetSubmit(
    CAMPAIGN_WIDGET_TOOL_NAMES.CONFIGURAR_AGENDAMENTO,
    toolCallId,
    (data) => buildScheduleConfigCompletionMessage(data.arts || []),
  );

  const normalizedArts = useMemo(() => {
    const mergedArts = mergeScheduleArtsWithSession(arts, sessionDetail);
    return mergedArts.map((art, index) => normalizeScheduleArt(art, index));
  }, [arts, sessionDetail]);

  const isScheduleWidgetReadOnly =
    isReadOnly ||
    isPending ||
    scheduleUx?.isPublicationReadOnly ||
    scheduleUx?.isSchedulingRunning;

  useEffect(() => {
    normalizedArts.forEach((art, index) => {
      ensureArtScheduleEntry(art, index);
    });
  }, [normalizedArts, ensureArtScheduleEntry]);

  return (
    <ScheduleSelector
      arts={normalizedArts}
      artScheduleData={artScheduleData}
      socialMediaSettings={socialScheduleContext.socialMediaSettings}
      isReadOnly={isScheduleWidgetReadOnly}
      onPreviewArt={(art, index) => {
        const previewHandler = onPreviewArt || setSchedulePreviewArt;
        previewHandler(art, index);
      }}
      onFinalize={(mergedArts) => {
        if (isScheduleWidgetReadOnly) return;
        const payloadArts = (mergedArts || []).map((art, index) =>
          normalizeScheduleArt(art, index),
        );
        handleComplete({arts: payloadArts});
      }}
    />
  );
}
